/**
 * Mobile-card counterpart of the desktop "Updates record" / "Conflict"
 * columns — existing-record chip + conflicting-property badges. Extracted
 * from RowTableUpdateCells.tsx so that file exports only helpers/constants
 * (react-refresh forbids mixing a component export with non-component
 * exports in one file).
 */

import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import type { PropertySchemaMap } from '@/lib/crm/hooks/usePropertySchema';

import { ConflictBadgeList } from './ConflictBadgeList';
import type { ConflictsByRow } from './RowTableUpdateCells';
import { EMPTY_CONFLICTS } from './RowTableUpdateCells';
import type { RowData } from './types';

interface UpdateRowFooterProps {
  row: RowData;
  orgId: number;
  conflictsByRow?: ConflictsByRow;
  schemaMap: PropertySchemaMap;
  newPropertyLabels: Record<string, string>;
}

/**
 * The two update-only facts, for the mobile card: which existing record this
 * row changes, and which of its properties disagree with that record.
 */
export function UpdateRowFooter({
  row,
  orgId,
  conflictsByRow,
  schemaMap,
  newPropertyLabels,
}: UpdateRowFooterProps) {
  const conflictSet = conflictsByRow?.[row.id];
  const conflicting =
    conflictSet && conflictSet.size > 0 ? [...conflictSet] : EMPTY_CONFLICTS;

  if (!row.existing_entity_id && conflicting.length === 0) {
    return null;
  }

  return (
    <div className='mt-1 flex flex-wrap items-center gap-2 border-t border-[var(--color-border-secondary)] pt-1.5'>
      {row.existing_entity_id && (
        <EntityBadgeById
          entityId={row.existing_entity_id}
          orgId={orgId}
          variant='xs'
        />
      )}
      <ConflictBadgeList
        conflictingProperties={conflicting}
        schemaMap={schemaMap}
        newPropertyLabels={newPropertyLabels}
      />
    </div>
  );
}
